/* eslint-disable @next/next/no-img-element */
import styles from "./styles.module.css";

import { useState, useRef, useEffect } from "react";
import { throttle } from "lodash";

import NextImage from "next/image";

import ZoomedBg from "./assets/zoomed_bg.png";

function getValue(val: number, min: number, max: number) {
  if (val < min) return min;
  if (val > max) return max;
  return val;
}

function getPosition(val: number, min: number, max: number) {
  var value = getValue(val, min, max);
  return value - min;
}

interface EdgeLimit {
  topMin: number;
  leftMin: number;
  topLimit: number;
  leftLimit: number;
}

interface ZoomedAreaStyle {
  transform: string;
}

interface ZoomedImageStyle {
  top: number | string;
  left: number | string;
}

interface PropsType {
  imgUrl: string; // 需要放大的图片路径
  children?: React.ReactNode; // 自定义放大插槽
  containerWidth: number; // 放大镜滑动区域宽度
  containerHeight: number; // 放大镜滑动区域高度
  zoomedAreaBgUrl?: string; // 放大镜背景图
  zoomedAreaWidth: number; // 放大镜显示宽度
  zoomedAreaHeight: number; // 放大镜显示高度
  zoomedImageBoxWidth: number; // 显示被放大图片盒子的宽度
}

const ImageZoom: React.FC<PropsType> = ({
  imgUrl,
  children,
  containerWidth,
  containerHeight,
  zoomedAreaBgUrl,
  zoomedAreaWidth,
  zoomedAreaHeight,
  zoomedImageBoxWidth,
}) => {
  const zoomedImageBoxHeight = (zoomedImageBoxWidth * zoomedAreaHeight) / zoomedAreaWidth;
  const zoomedImageWidth = zoomedImageBoxWidth * (containerWidth / zoomedAreaWidth);
  const zoomedImageHeight = zoomedImageBoxHeight * (containerHeight / zoomedAreaHeight);
  const topMin = zoomedAreaHeight / 2;
  const leftMin = zoomedAreaWidth / 2;

  const imageZoomBox = useRef<HTMLDivElement>(null);
  const imageZoomBoxRect = useRef<DOMRect | null>(null);
  const edgeLimit = useRef<EdgeLimit>({
    topMin,
    leftMin,
    topLimit: containerHeight - topMin,
    leftLimit: containerWidth - leftMin,
  });
  const zoomedArea = useRef<HTMLDivElement>(null);

  const [isActive, setIsActive] = useState(false);
  const [zoomedAreaStyle, setZoomedAreaStyle] = useState<ZoomedAreaStyle>({
    transform: "translate(0px, 0px)",
  });

  const [zoomedImageStyle, setZoomedImageStyle] = useState<ZoomedImageStyle>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    setImageZoomBoxRect();

    const handlePageScroll = throttle(() => {
      setImageZoomBoxRect();
    }, 500);

    window.addEventListener("scroll", handlePageScroll);

    const handlePageResize = throttle(() => {
      setImageZoomBoxRect();
    }, 500);

    window.addEventListener("resize", handlePageResize);

    return () => {
      window.removeEventListener("scroll", handlePageScroll);
      window.removeEventListener("resize", handlePageResize);
    };
  }, []);

  const setImageZoomBoxRect = () => {
    if (!imageZoomBox.current) return;
    imageZoomBoxRect.current = imageZoomBox.current.getBoundingClientRect();
  };

  const handleZoomAreaMouseEnter = () => {
    if (!imgUrl || !imageZoomBoxRect.current) return
    setIsActive(true);
  };

  const handleZoomAreaMouseMove = throttle((e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgUrl || !imageZoomBoxRect.current || !edgeLimit.current) return
    if (!isActive) setIsActive(true);
    const { width, height, top, left } = imageZoomBoxRect.current as DOMRect;
    const { topMin, topLimit, leftMin, leftLimit } = edgeLimit.current as EdgeLimit;
    const offsetX = getPosition(e.clientX - left, leftMin, leftLimit);
    const offsetY = getPosition(e.clientY - top, topMin, topLimit);
    setZoomedAreaStyle({ transform: `translate(${offsetX}px, ${offsetY}px)` });

    const backgroundTop = (offsetY / height) * zoomedImageHeight;
    const backgroundLeft = (offsetX / width) * zoomedImageWidth;
    setZoomedImageStyle({ top: `-${backgroundTop}px`, left: `-${backgroundLeft}px` });
  }, 500);

  const handleZoomAreaMouseLeave = () => {
    setIsActive(false);
  };

  return (
    <>
      <div
        className={styles.image_zoom}
        style={{
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
        }}
        ref={imageZoomBox}
      >
        <div
          className={styles.original_image_box}
          onMouseEnter={handleZoomAreaMouseEnter}
          onMouseMove={handleZoomAreaMouseMove}
          onMouseLeave={handleZoomAreaMouseLeave}
        >
          {children ? children : <img className={styles.original_image} src={imgUrl} alt="" />}

          <div
            className={styles.zoomed_area}
            ref={zoomedArea}
            style={{
              ...zoomedAreaStyle,
              display: isActive ? "block" : "none",
            }}
          >
            {zoomedAreaBgUrl ? <img src={zoomedAreaBgUrl} alt="zoomed bg image" /> : <NextImage src={ZoomedBg} alt="zoomed bg image" />}
          </div>
        </div>

        <div
          className={styles.zoomed_image_box}
          style={{
            display: isActive ? "block" : "none",
            width: `${zoomedImageBoxWidth}px`,
            height: `${zoomedImageBoxHeight}px`,
          }}
        >
          <div
            className={styles.zoomed_image_inner}
            style={{
              width: `${zoomedImageWidth}px`,
              height: `${zoomedImageHeight}px`,
              ...zoomedImageStyle,
            }}
          >
            <img className={styles.zoomed_image} src={imgUrl} alt="" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageZoom;
